/* ===========================================================================
   iEDM — theme-native fallback for Rebuy widgets (rebuy-fallback.js)
   ===========================================================================
   Rebuy stays primary. This file only fills the slot next to a Rebuy mount when
   Rebuy has not rendered any product there, using first-party Shopify routes.

   WHY (dup 189272522827, 2026-10-19 r2, docs/archive/dup272/r2-rebuy-blockers.md):
   every Rebuy widget loads through the same rebuy.js -> rebuyengine.com path; the
   only Rebuy request on Brave's default lists is EasyPrivacy's analytics-event
   rule (rebuyengine.com/api/<any>/analytics/event/), which stops the product-viewed
   events that feed Rebuy's server-side "Recently Viewed" history. The cart
   widget's rule set also outputs /products/similar_products, so it still has
   products; the collection rule set outputs /products/viewed only, so it is
   empty. The PDP mount (144447) is the placeholder of Rebuy A/B experiment 3393:
   Rebuy inserts the assigned variant inside it, 95141 "You may also like"
   (similar products, renders on Brave) or 144446 "Recently Viewed" (history,
   empty on Brave). r4: the PDP fallback follows that assignment via opts.key.

   TRIGGER. Wait for window load. If window.Rebuy is missing then (script blocked
   or failed), fill at once. Otherwise poll the mount for `.rebuy-product-block`
   (the cell class Rebuy's carousels select on) for WAIT_MS; if none appears,
   fill. If Rebuy renders products later, the fallback is removed again, so the
   two never show together. refresh() (bfcache restore) refills at once only when
   the fallback is already showing; otherwise it restarts the same timed wait, so
   Rebuy's own refetch gets its full window first.

   REQUESTS. Only same-origin paths under Shopify.routes.root:
     recommendations/products?product_id=..&limit=8&section_id=rebuy-fallback-recs
     products/<handle>?section_id=rebuy-fallback-card
   No analytics, no third-party host. Cards come from the theme's own card
   partial (snippets/product-listing-carousel.liquid), so stars arrive the same
   way as on every other carousel card (assets/yotpo-stars.js).

   RECENTLY VIEWED. A first-party list of product handles in localStorage
   (`iedm_rv`, newest first, capped at RV_CAP), written on each PDP view. It never
   leaves the device. Not written when Shopify's Customer Privacy API is loaded
   and reports preferences processing as not allowed. Every storage access is
   wrapped in try/catch: a throwing or missing localStorage means "no history".
   =========================================================================== */
(function (W) {
  'use strict';
  if (W.iedmRebuyFallback) return;

  var D = W.document;
  var RV_KEY = 'iedm_rv';
  var RV_CAP = 12;
  var WAIT_MS = 4000;
  var POLL_MS = 250;
  var REBUY_CELL = '.rebuy-product-block';
  var HANDLE_RE = /^[a-z0-9][a-z0-9_-]{0,254}$/;

  var CSS =
    '.rbfb{margin:30px 0}' +
    '.rbfb[hidden]{display:none}' +
    '.rbfb-title{margin:0 0 15px;text-align:center;text-transform:uppercase;font-size:20px}' +
    '.rbfb-carousel.slick-initialized{display:block}' +
    '.rbfb-carousel:not(.slick-initialized){display:flex;flex-wrap:nowrap;overflow-x:auto;-webkit-overflow-scrolling:touch}' +
    '.rbfb-carousel:not(.slick-initialized)>*{flex:0 0 16.6667%;max-width:16.6667%;min-width:0}' +
    '.rbfb-carousel button.slick-prev,.rbfb-carousel button.slick-next{top:40%}' +
    '.rbfb-carousel button.slick-prev{left:0;z-index:2}' +
    '.rbfb-carousel button.slick-next{right:0;z-index:2}' +
    '.rbfb-carousel button.slick-prev:before,.rbfb-carousel button.slick-next:before{color:#444;font-family:fontAwesome;font-weight:800;font-size:20px;opacity:1}' +
    '.rbfb-carousel button.slick-prev:before{content:"\\f104"}' +
    '.rbfb-carousel button.slick-next:before{content:"\\f105"}' +
    '.rbfb-carousel .product-info{margin-bottom:5px}' +
    '@media screen and (max-width:1214.98px){.rbfb-carousel:not(.slick-initialized)>*{flex-basis:25%;max-width:25%}}' +
    '@media screen and (max-width:994.98px){.rbfb-carousel:not(.slick-initialized)>*{flex-basis:33.3333%;max-width:33.3333%}}' +
    '@media screen and (max-width:749.98px){.rbfb-carousel:not(.slick-initialized)>*{flex-basis:50%;max-width:50%}}' +
    '@media screen and (max-width:640px){.rbfb-title{font-size:18px}.rbfb-carousel button.slick-prev{left:-10px;z-index:1}.rbfb-carousel button.slick-next{right:-10px;z-index:1}}';

  /* Same options as assets/carousel-2-init.js, so the row matches the home carousels
     and the pre-init flex ladder above. */
  var SLICK = {
    dots: false, infinite: false, speed: 600, touchThreshold: 100, waitForAnimate: false,
    slidesToShow: 6, slidesToScroll: 1,
    responsive: [
      { breakpoint: 1215, settings: { slidesToShow: 4, slidesToScroll: 1, infinite: true, dots: true, touchThreshold: 10, waitForAnimate: false } },
      { breakpoint: 995, settings: { slidesToShow: 3, slidesToScroll: 1, touchThreshold: 10, waitForAnimate: false } },
      { breakpoint: 750, settings: { slidesToShow: 2, slidesToScroll: 2, touchThreshold: 10, waitForAnimate: false } }
    ]
  };

  function root() {
    var r = W.Shopify && W.Shopify.routes && W.Shopify.routes.root;
    return typeof r === 'string' && r.charAt(0) === '/' && r.charAt(1) !== '/' ? r.replace(/\/?$/, '/') : '/';
  }

  function storage() {
    try { return W.localStorage || null; } catch (e) { return null; }
  }

  function readRV() {
    try {
      var s = storage();
      var list = s ? JSON.parse(s.getItem(RV_KEY) || '[]') : [];
      return Array.isArray(list) ? list.filter(function (h) { return typeof h === 'string' && HANDLE_RE.test(h); }) : [];
    } catch (e) {
      return [];
    }
  }

  function preferencesAllowed() {
    try {
      var cp = W.Shopify && W.Shopify.customerPrivacy;
      if (cp && typeof cp.preferencesProcessingAllowed === 'function') return cp.preferencesProcessingAllowed() !== false;
    } catch (e) {}
    return true;
  }

  function recordView(handle) {
    if (typeof handle !== 'string' || !HANDLE_RE.test(handle) || !preferencesAllowed()) return;
    try {
      var s = storage();
      if (!s) return;
      var list = readRV().filter(function (h) { return h !== handle; });
      list.unshift(handle);
      s.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_CAP)));
    } catch (e) {}
  }

  function getText(path) {
    return W.fetch(root() + path, { credentials: 'same-origin' }).then(function (r) {
      return r.ok ? r.text() : '';
    });
  }

  function itemsFrom(html) {
    if (!html) return [];
    var doc = new W.DOMParser().parseFromString(html, 'text/html');
    return Array.prototype.slice.call(doc.querySelectorAll('.rbfb-item'));
  }

  function loadRecommendations(productId) {
    if (!/^\d+$/.test(String(productId))) return Promise.resolve([]);
    return getText('recommendations/products?product_id=' + productId + '&limit=8&section_id=rebuy-fallback-recs').then(itemsFrom);
  }

  function loadRecentlyViewed(exclude) {
    var handles = readRV().filter(function (h) { return h !== exclude; });
    if (!handles.length) return Promise.resolve([]);
    return Promise.all(handles.map(function (h) {
      return getText('products/' + encodeURIComponent(h) + '?section_id=rebuy-fallback-card').then(itemsFrom, function () { return []; });
    })).then(function (lists) {
      return lists.reduce(function (all, l) { return all.concat(l.slice(0, 1)); }, []);
    });
  }

  function ensureStyle() {
    if (D.getElementById('rbfb-style')) return;
    var st = D.createElement('style');
    st.id = 'rbfb-style';
    st.textContent = CSS;
    D.head.appendChild(st);
  }

  function rebuyRendered(mount) {
    return !!(mount && mount.querySelector(REBUY_CELL));
  }

  function teardown(slot) {
    var $ = W.jQuery;
    var track = slot.querySelector('.rbfb-carousel.slick-initialized');
    if (track && $ && $.fn && $.fn.slick) {
      try { $(track).slick('unslick'); } catch (e) {}
    }
    slot.innerHTML = '';
  }

  function clear(slot) {
    teardown(slot);
    slot.hidden = true;
  }

  function render(slot, items) {
    ensureStyle();
    var title = D.createElement(slot.getAttribute('data-title-tag') === 'h3' ? 'h3' : 'h2');
    title.className = 'rbfb-title';
    title.textContent = slot.getAttribute('data-title') || '';
    var wrap = D.createElement('section');
    wrap.className = 'homepage-section home-carousel c2';
    var track = D.createElement('div');
    track.className = 'rbfb-carousel';
    items.forEach(function (n) { track.appendChild(D.importNode(n, true)); });
    wrap.appendChild(track);
    teardown(slot);
    slot.appendChild(title);
    slot.appendChild(wrap);
    slot.hidden = false;
    var $ = W.jQuery;
    if ($ && $.fn && $.fn.slick) {
      try { $(track).slick(SLICK); } catch (e) {}
    }
  }

  /* opts: { mount: Rebuy mount element, slot: fallback container, load: () => Promise<Element[]>,
     key: optional () => string naming what load() would fetch right now (r4: the Rebuy experiment
     variant Rebuy inserted in the mount). When the key changes after a fill, the slot is refilled,
     so the fallback always follows Rebuy's own assignment. Read-only: nothing here writes to Rebuy. } */
  function watch(opts) {
    var mount = opts.mount;
    var slot = opts.slot;
    if (!mount || !slot) return null;
    var shown = false;
    var loading = false;
    var attempted = false;
    var lastKey = '';
    var timer = null;

    function keyNow() {
      try { return opts.key ? String(opts.key()) : ''; } catch (e) { return ''; }
    }

    function fill() {
      if (loading || rebuyRendered(mount)) return Promise.resolve(false);
      loading = true;
      attempted = true;
      var key = lastKey = keyNow();
      return Promise.resolve()
        .then(opts.load)
        .then(function (items) {
          loading = false;
          if (keyNow() !== key) return fill();
          if (rebuyRendered(mount)) return false;
          if (!items || !items.length) {
            if (shown) clear(slot);
            shown = false;
            return false;
          }
          render(slot, items);
          shown = true;
          return true;
        }, function () {
          loading = false;
          return false;
        });
    }

    function onRebuyChange() {
      if (rebuyRendered(mount)) {
        if (timer) { W.clearInterval(timer); timer = null; }
        if (shown) { clear(slot); shown = false; }
      } else if (attempted && !loading && keyNow() !== lastKey) {
        fill();
      }
    }

    if (typeof W.MutationObserver === 'function') {
      new W.MutationObserver(onRebuyChange).observe(mount, { childList: true, subtree: true });
    }

    function start() {
      if (timer) { W.clearInterval(timer); timer = null; }
      if (!W.Rebuy) return fill();
      var waited = 0;
      return new Promise(function (resolve) {
        timer = W.setInterval(function () {
          waited += POLL_MS;
          if (rebuyRendered(mount)) {
            W.clearInterval(timer); timer = null;
            resolve(false);
          } else if (waited >= WAIT_MS) {
            W.clearInterval(timer); timer = null;
            resolve(fill());
          }
        }, POLL_MS);
      });
    }

    var started = new Promise(function (resolve) {
      if (D.readyState === 'complete') resolve(start());
      else W.addEventListener('load', function () { resolve(start()); }, { once: true });
    });

    return {
      started: started,
      refresh: function () { return shown ? fill() : start(); },
      isShown: function () { return shown; }
    };
  }

  W.iedmRebuyFallback = {
    recordView: recordView,
    readRecentlyViewed: readRV,
    loadRecommendations: loadRecommendations,
    loadRecentlyViewed: loadRecentlyViewed,
    watch: watch
  };
})(window);
